import { ChartData } from '@crm/api'
import { getColor } from '@crm/components/Charts/utils'
import { ChartDataItem } from '@crm/features/reports/types'
import { abbreviateNumber } from '@crm/utils'
import { Text } from '@shared/components'
import { Card, Empty, Flex } from 'antd'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

type Props = {
  label?: string
  isLoading?: boolean
  data?: ChartData
  xAxy?: string
  yAxy?: string
}

export const LineChart = ({ label, isLoading, data, xAxy, yAxy }: Props) => {
  const dataMapping = data?.data as ChartDataItem[]

  return (
    <Card title={label} loading={isLoading}>
      {data ? (
        <div className="w-full" style={{ height: 360 }}>
          <ResponsiveContainer width="100%" height="100%" minHeight={360}>
            <ComposedChart data={dataMapping}>
              <defs>
                {data?.mapping?.map((_, index) => (
                  <linearGradient
                    key={index}
                    id={`colorLine${index}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={getColor(index + 4)}
                      stopOpacity={0.4}
                    />
                    <stop
                      offset="95%"
                      stopColor={getColor(index + 4)}
                      stopOpacity={0}
                    />
                  </linearGradient>
                ))}
              </defs>

              <CartesianGrid vertical={false} strokeDasharray="4 0" />
              <XAxis dataKey={xAxy || 'name'} />
              <YAxis
                dataKey={yAxy}
                yAxisId="left"
                orientation="left"
                scale="auto"
                type="number"
                tickFormatter={(value) => abbreviateNumber(value)}
              />

              <Tooltip
                content={({ active, payload }) => {
                  return active && payload ? (
                    <Flex vertical gap={4}>
                      <Text>{payload[0].payload.date}</Text>
                      {data?.mapping
                        ?.map((item) => {
                          return {
                            label: item.label,
                            value: payload[0].payload[item?.field_name || '']
                          }
                        })
                        .sort(function (a, b) {
                          return b.value - a.value
                        })
                        .map((item, index) => (
                          <p key={index}>
                            {item.label}: {item.value}
                          </p>
                        ))}
                    </Flex>
                  ) : null
                }}
              />

              {data?.mapping?.map((item, index) => (
                <Area
                  type="monotone"
                  key={index}
                  yAxisId="left"
                  dataKey={item.field_name || ''}
                  stroke={getColor(index + 4)}
                  fillOpacity={1}
                  fill={`url(#colorLine${index})`}
                  activeDot={{ stroke: getColor(index + 4), strokeWidth: 4 }}
                  dot={{
                    stroke: 'white',
                    strokeWidth: 1,
                    r: 3,
                    fill: getColor(index + 4)
                  }}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Empty />
      )}
    </Card>
  )
}
